import { numberLocaleFor } from "../i18n/locale";
import type { Locale } from "../i18n/locale";
import { uiFormat } from "../i18n/ui";
import type { UiKey } from "../i18n/ui";
import type { ScenarioSnapshot } from "../lib/scenario";

type Props = {
  locale: Locale;
  t: (key: UiKey) => string;
  scenarioA: ScenarioSnapshot | null;
  scenarioB: ScenarioSnapshot | null;
  currentTotalUsd: number;
  onSaveA: () => void;
  onSaveB: () => void;
  onClearA: () => void;
  onClearB: () => void;
};

export function ScenarioCompare({
  locale,
  t,
  scenarioA,
  scenarioB,
  currentTotalUsd,
  onSaveA,
  onSaveB,
  onClearA,
  onClearB,
}: Props) {
  const nf = numberLocaleFor(locale);
  const money = (n: number) =>
    n.toLocaleString(nf, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });

  function meta(s: ScenarioSnapshot) {
    return uiFormat(locale, "scenario.snapshotMeta", {
      u: s.userMult,
      r: s.reqMult,
      usd: money(s.totalMonthlyUsd),
    });
  }

  const deltaAB =
    scenarioA && scenarioB
      ? scenarioB.totalMonthlyUsd - scenarioA.totalMonthlyUsd
      : null;

  return (
    <div>
      <h2>{t("scenario.title")}</h2>
      <div className="actions">
        <button type="button" className="btn" onClick={onSaveA}>
          {t("scenario.saveA")}
        </button>
        <button type="button" className="btn" onClick={onSaveB}>
          {t("scenario.saveB")}
        </button>
        <button
          type="button"
          className="btn ghost"
          onClick={onClearA}
          disabled={!scenarioA}
        >
          {t("scenario.clearA")}
        </button>
        <button
          type="button"
          className="btn ghost"
          onClick={onClearB}
          disabled={!scenarioB}
        >
          {t("scenario.clearB")}
        </button>
      </div>

      {!scenarioA && !scenarioB ? (
        <p className="empty">{t("scenario.empty")}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th />
                <th>A</th>
                <th>B</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t("scenario.total")}</td>
                <td>{scenarioA ? money(scenarioA.totalMonthlyUsd) : "—"}</td>
                <td>{scenarioB ? money(scenarioB.totalMonthlyUsd) : "—"}</td>
              </tr>
              {deltaAB !== null && (
                <tr>
                  <td>{t("scenario.deltaAB")}</td>
                  <td colSpan={2} className="mono">
                    {money(deltaAB)}
                  </td>
                </tr>
              )}
              <tr>
                <td>{t("scenario.deltaVsCurrent")}</td>
                <td className="mono">
                  {scenarioA ? money(currentTotalUsd - scenarioA.totalMonthlyUsd) : "—"}
                </td>
                <td className="mono">
                  {scenarioB ? money(currentTotalUsd - scenarioB.totalMonthlyUsd) : "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {(scenarioA || scenarioB) && (
        <ul className="meta">
          {scenarioA && (
            <li>
              <strong>A:</strong> {meta(scenarioA)}
            </li>
          )}
          {scenarioB && (
            <li>
              <strong>B:</strong> {meta(scenarioB)}
            </li>
          )}
        </ul>
      )}

      <style>{`
        h2 {
          font-size: 1rem;
          margin: 0 0 0.75rem;
        }
        .actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.45rem;
          margin-bottom: 1rem;
        }
        .btn {
          border: 1px solid var(--border);
          background: var(--surface2);
          color: var(--text);
          padding: 0.35rem 0.65rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.8rem;
          font-family: var(--font);
        }
        .btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .btn.ghost {
          border-style: dashed;
        }
        .empty {
          color: var(--muted);
          font-size: 0.9rem;
          margin: 0;
        }
        .table-wrap {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.88rem;
        }
        th,
        td {
          text-align: left;
          padding: 0.45rem 0.6rem;
          border-bottom: 1px solid var(--border);
        }
        th {
          color: var(--muted);
          font-weight: 600;
        }
        .mono {
          font-family: var(--mono);
          color: var(--accent);
        }
        .meta {
          margin: 1rem 0 0;
          padding-left: 1.1rem;
          font-size: 0.78rem;
          color: var(--muted);
        }
      `}</style>
    </div>
  );
}
